import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, View } from 'react-native';
import { PIPWidget, TWCText } from '@components';
import { useRecoilValue } from 'recoil';

import { assets } from '@assets';
import { dailyWeatherPageTestIDs } from '@constants';
import { useContextReference, useNodeHandle } from '@hooks';
import { DailyWeatherBottomRow } from '@screens';
import { DailyForecastData, screenReaderEnabledState } from '@store';

import { styles } from './styles';

import { VideoContext } from '../../../hooks/useIsVideoFocused';
import { DailyWeatherMiddleRow } from '../DailyWeatherMiddleRow/DailyWeatherMiddleRow';
import { DailyWeatherTopRow } from '../DailyWeatherTopRow/DailyWeatherTopRow';

const errorTPath = 'errors.';

type DailyWeatherPageContentProps = {
  data?: DailyForecastData;
  error: unknown;
  isLoading: boolean;
};

export const DailyWeatherPageContent: FC<DailyWeatherPageContentProps> = ({ data, error, isLoading }) => {
  const { t } = useTranslation();
  const [focusIndex, setFocusIndex] = useState(0);
  const topRowReference = useContextReference(dailyWeatherPageTestIDs.dailyWeatherTopRow);
  const middleRowReference = useContextReference(dailyWeatherPageTestIDs.dailyWeatherMiddleRowToday);
  const bottomRowReference = useContextReference(dailyWeatherPageTestIDs.dailyWeatherTiles);

  const screenReaderEnabled = useRecoilValue(screenReaderEnabledState);

  const topRowHandle = useNodeHandle(topRowReference);
  const middleRowHandle = useNodeHandle(middleRowReference);
  const bottomRowHandle = useNodeHandle(bottomRowReference);
  const bottomRowUpNode = screenReaderEnabled ? middleRowHandle : null;

  const isErrorLoadingData = error || data === undefined;

  return (
    <>
      <PIPWidget
        height={160}
        pageID={dailyWeatherPageTestIDs.dailyWeatherPage}
        position={'topRight'}
        testID={dailyWeatherPageTestIDs.pipWidget}
        videoContext={VideoContext.dailyForecast}
        width={284}
      />
      <View style={styles.wrapper}>
        {isErrorLoadingData ? (
          <View style={styles.dailyPageErrorWrapper}>
            <Image source={assets.sadCloud} style={styles.dailyPageErrorImage} />
            <TWCText noTestID style={styles.dailyPageErrorText} variant={'mediumText40'}>
              {t(`${errorTPath}noDataText`)}
            </TWCText>
          </View>
        ) : (
          <>
            <DailyWeatherTopRow
              data={data}
              defaultFocus={bottomRowReference}
              index={focusIndex}
              nextFocusDown={middleRowHandle}
            />
            <DailyWeatherMiddleRow
              data={data}
              defaultFocus={bottomRowReference}
              error={error}
              index={focusIndex}
              isLoading={isLoading}
              nextFocusDown={bottomRowHandle}
              nextFocusUp={topRowHandle}
            />
            <DailyWeatherBottomRow
              data={data}
              error={error}
              focusIndexChanged={setFocusIndex}
              isLoading={isLoading}
              nextFocusUp={bottomRowUpNode}
            />
          </>
        )}
      </View>
    </>
  );
};
